import { useMemo, useState, useEffect } from "react"
import { FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material"
import { SelectChangeEvent } from "@mui/material/Select"
import { User } from "../../../../shared/types/user"
import { getMe, getUsers, deleteUser, deleteUsersBulk } from "../../../../features/search/service/user-service"
import { getCompaniesListSelect } from "../../../../features/search/service/company-service"
import { SelectOption } from "../../../../shared/types/select-options"
import { MenuTop } from "../../../../components/menu/top/menu-top"
import "./styles/list.css"
import ButtonComponent from "../../../../components/button/button"
import { useNavigate } from "react-router-dom"
import Icons from "../../../../components/Icon/icons"
import { ConfirmDialog } from "../../../../components/modal/confirmation"
import { SelectableList } from "../../../../components/selectable-list/selectable-list"
import { useTranslation } from "../../../../shared/i18n/i18n-context"

const SUPER_ADMIN_ROLE = "super-admin"

const getRoleLabel = (user: User, fallback: string) => {
    return user.role?.name || user.role?.description || fallback
}

const getCompanyLabel = (user: User, fallback: string) => {
    return user.company?.name || user.company?.description || fallback
}

export const AdminUsersList = () => {
    const navigate = useNavigate();
    const { t } = useTranslation('admin');
    const [users, setUsers] = useState<User[]>([])
    const [companies, setCompanies] = useState<SelectOption[]>([])
    const [companyFilter, setCompanyFilter] = useState("")
    const [searchTerm, setSearchTerm] = useState("")
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
    const [me, setMe] = useState<User | null>(null)
    const [loading, setLoading] = useState(true)
    const [dialogLoading, setDialogLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [openDialog, setOpenDialog] = useState(false)
    const [openBulkDialog, setOpenBulkDialog] = useState(false)
    const [userToDelete, setUserToDelete] = useState<User | null>(null)

    const isSuperAdmin = me?.role?.name === SUPER_ADMIN_ROLE

    const fetchUsers = async (selectedCompanyId = companyFilter, userIsSuperAdmin = isSuperAdmin) => {
        const usersData = await getUsers(userIsSuperAdmin ? selectedCompanyId || undefined : undefined)
        setUsers(usersData)
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true)
                setError(null)

                const userData = await getMe()
                const userIsSuperAdmin = userData.role?.name === SUPER_ADMIN_ROLE

                const [usersData, companiesData] = await Promise.all([
                    getUsers(),
                    userIsSuperAdmin ? getCompaniesListSelect() : Promise.resolve([])
                ])

                setMe(userData)
                setUsers(usersData)
                setCompanies(companiesData)
            } catch (error) {
                console.error('❌ Erro ao buscar dados:', error)
                setError(t('usersLoadError'))
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [t])

    const filteredUsers = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase()

        if (!normalizedSearch) {
            return users
        }

        return users.filter((user) => {
            const searchableText = [
                user.name,
                user.email,
                getRoleLabel(user, t('noRole')),
                user.role_id,
                getCompanyLabel(user, t('noCompany'))
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()

            return searchableText.includes(normalizedSearch)
        })
    }, [searchTerm, t, users])

    useEffect(() => {
        const visibleSelectableIds = new Set(
            filteredUsers
                .filter((user) => user.id && user.id !== me?.id)
                .map((user) => user.id as string)
        )

        setSelectedUserIds((currentSelectedIds) =>
            currentSelectedIds.filter((id) => visibleSelectableIds.has(id))
        )
    }, [filteredUsers, me?.id])

    const selectedUsers = useMemo(() => {
        const selectedSet = new Set(selectedUserIds)
        return filteredUsers.filter((user) => user.id && selectedSet.has(user.id))
    }, [filteredUsers, selectedUserIds])

    const handleCompanyFilterChange = async (event: SelectChangeEvent) => {
        const selectedCompanyId = event.target.value

        setCompanyFilter(selectedCompanyId)
        setSelectedUserIds([])

        try {
            setLoading(true)
            setError(null)
            await fetchUsers(selectedCompanyId, true)
        } catch (error) {
            console.error('❌ Erro ao filtrar usuários:', error)
            setError(t('usersFilterError'))
        } finally {
            setLoading(false)
        }
    }

    const handleOpenDialog = (user: User) => {
        setUserToDelete(user)
        setOpenDialog(true)
    }

    const handleCloseDialog = () => {
        setOpenDialog(false)
        setUserToDelete(null)
    }

    const handleOpenBulkDialog = () => {
        if (selectedUserIds.length === 0) return
        setOpenBulkDialog(true)
    }

    const handleCloseBulkDialog = () => {
        setOpenBulkDialog(false)
    }

    const handleConfirmDelete = async () => {
        if (!userToDelete?.id) return

        try {
            setDialogLoading(true)
            setError(null)

            await deleteUser(userToDelete.id)
            await fetchUsers()

            handleCloseDialog()
        } catch (error) {
            console.error('❌ Erro ao deletar usuário:', error)
            setError(t('userDeleteError'))
        } finally {
            setDialogLoading(false)
        }
    }

    const handleConfirmBulkDelete = async () => {
        const idsToDelete = selectedUserIds.filter((id) => id !== me?.id)

        if (idsToDelete.length === 0) {
            return
        }

        try {
            setDialogLoading(true)
            setError(null)

            await deleteUsersBulk(idsToDelete)
            setSelectedUserIds([])
            await fetchUsers()

            handleCloseBulkDialog()
        } catch (error) {
            console.error('❌ Erro ao deletar usuários:', error)
            setError(t('usersBulkDeleteError'))
        } finally {
            setDialogLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="admin-users-page">
                <MenuTop />
                <div className="users-loading">
                    <div className="users-loading-text">{t('loadingUsers')}</div>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="admin-users-page">
                <MenuTop />
                <div className="users-error">
                    <div className="users-error-text">{error}</div>
                </div>
            </div>
        )
    }

    return (
        <div className="admin-users-page">
            <MenuTop />
            <div className="users-container">
                <div className="users-header">
                    <h1 className="users-title">{t('users')}</h1>
                    <p className="users-subtitle">
                        {filteredUsers.length} {filteredUsers.length === 1 ? t('userSingular') : t('userPlural')} {filteredUsers.length === 1 ? t('foundSingular') : t('foundPlural')}
                    </p>
                </div>

                <div className="users-filters">
                    <TextField
                        label={t('searchUsers')}
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        fullWidth
                    />

                    {isSuperAdmin && (
                        <FormControl fullWidth>
                            <InputLabel id="users-company-filter-label">{t('company')}</InputLabel>
                            <Select
                                labelId="users-company-filter-label"
                                value={companyFilter}
                                label={t('company')}
                                onChange={handleCompanyFilterChange}
                            >
                                <MenuItem value="">{t('allCompanies')}</MenuItem>
                                {companies.map((company) => (
                                    <MenuItem key={company.id} value={String(company.id)}>
                                        {company.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </div>

                <SelectableList
                    items={filteredUsers}
                    selectedIds={selectedUserIds}
                    onSelectedIdsChange={setSelectedUserIds}
                    getItemId={(user) => user.id}
                    listClassName="users-grid"
                    itemClassName="user-card"
                    selectAllLabel={t('selectAll')}
                    bulkActionLabel={t('deleteSelected')}
                    onBulkAction={handleOpenBulkDialog}
                    getItemDisabled={(user) => user.id === me?.id}
                    getItemDisabledReason={(user) => user.id === me?.id ? t('cannotDeleteOwnAccount') : undefined}
                    emptyState={(
                        <div className="users-empty">
                            <div className="users-empty-text">
                                {t('noUsersFound')}
                            </div>
                        </div>
                    )}
                    renderItem={(user) => (
                        <>
                            <div className="user-card-header">
                                <div className="user-avatar">
                                    {user.name?.charAt(0).toUpperCase() || '?'}
                                </div>
                                <div className="user-info">
                                    <h3 className="user-name">
                                        {user.name}
                                    </h3>
                                    <p className="user-email">{user.email}</p>
                                </div>
                                <Icons
                                    iconClicked={() => handleOpenDialog(user)}
                                    iconName="DeleteOutline"
                                    addTooltip={t('deleteUser')}
                                />
                            </div>

                            <div className="user-details">
                                <div className="user-detail-row">
                                    <span className="user-detail-label">{t('role')}:</span>
                                    <span className="user-detail-value user-role">
                                        {getRoleLabel(user, t('noRole'))}
                                    </span>
                                </div>

                                {isSuperAdmin && (
                                    <div className="user-detail-row">
                                        <span className="user-detail-label">{t('company')}:</span>
                                        <span className="user-detail-value">
                                            {getCompanyLabel(user, t('noCompany'))}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="user-card-footer">
                                <ButtonComponent
                                    label={t('viewDetails')}
                                    onClick={() => navigate(`/admin-painel/users/edit/${user.id}`)}
                                />
                            </div>
                        </>
                    )}
                />
            </div>

            <ConfirmDialog
                open={openDialog}
                title={t('deleteUser')}
                message={t('deleteUserMessage').replace('{name}', userToDelete?.name || '')}
                confirmText={t('delete')}
                cancelText={t('cancel')}
                onConfirm={handleConfirmDelete}
                onCancel={handleCloseDialog}
                confirmColor="error"
                loading={dialogLoading}
            />

            <ConfirmDialog
                open={openBulkDialog}
                title={t('deleteSelectedUsers')}
                message={t('deleteSelectedUsersMessage')
                    .replace('{count}', String(selectedUsers.length))
                    .replace('{plural}', selectedUsers.length !== 1 ? t('selectedPluralSuffix') : '')}
                confirmText={t('deleteSelected')}
                cancelText={t('cancel')}
                onConfirm={handleConfirmBulkDelete}
                onCancel={handleCloseBulkDialog}
                confirmColor="error"
                loading={dialogLoading}
            />
        </div>
    )
}
